import axios, { AxiosHeaders, type AxiosRequestHeaders } from 'axios';
import { getSession, getSessionUser, redirectToLogin } from './session';
import { getVendorContextHeaderValue } from './vendorContext';
import { toast } from './toast';
import { normalizeRole } from '@/auth/permissions';

declare const VITE_API_BASE: string | undefined;

export class HttpError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

const rawApiBase =
  (import.meta as any)?.env?.VITE_API_BASE ??
  (typeof VITE_API_BASE !== 'undefined' ? (VITE_API_BASE as string) : undefined);

const fallbackBase =
  typeof rawApiBase === 'string' && rawApiBase.trim().length > 0 ? rawApiBase.trim() : '/api';
export const API_BASE = (fallbackBase.replace(/\/+$/, '') || '/api') as string;

export type ApiOk<T> = { ok: true; data: T; status: number };
export type ApiErr = { ok: false; code: string; message: string; details?: unknown; status: number };
export type ApiResp<T> = ApiOk<T> | ApiErr;

const BASE = API_BASE || '/api';
const AUTH_ENDPOINTS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/me'];
const ALMACEN_ID_KEY = 'inv:almacenId';
const ALMACEN_CODE_KEY = 'inventario:almacen';
const ALMACEN_MAP_KEY = 'inv:almacenMap';

function idemKey(): string {
  const hasCrypto = typeof globalThis !== 'undefined' && typeof globalThis.crypto !== 'undefined';
  if (hasCrypto && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random()}`;
}

function readStoredAlmacenId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ALMACEN_ID_KEY);
    if (!raw) return null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? String(num) : null;
  } catch {
    return null;
  }
}

function readStoredAlmacenFromCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const code = window.localStorage.getItem(ALMACEN_CODE_KEY);
    if (!code) return null;
    const rawMap = window.localStorage.getItem(ALMACEN_MAP_KEY);
    if (!rawMap) return null;
    const map = JSON.parse(rawMap) as Record<string, number>;
    const id = map[code.trim().toUpperCase()];
    return Number.isFinite(id) && id > 0 ? String(id) : null;
  } catch {
    return null;
  }
}

function resolveAdminAlmacenId(): string | null {
  return readStoredAlmacenId() || readStoredAlmacenFromCode();
}

function redirectExternalReadOnlyRole(role: string): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';

  if (role === 'CLIENTE') {
    const allowed = path === '/cliente' || path.startsWith('/cliente/');
    if (!allowed) {
      window.location.assign('/cliente');
    }
    return;
  }

  if (role === 'CONSULTA' || role === 'PROVEEDOR') {
    const allowed =
      path === '/inventario' ||
      path.startsWith('/inventario/') ||
      path === '/precios' ||
      path.startsWith('/precios/') ||
      path === '/proveedor/agenda' ||
      path.startsWith('/proveedor/agenda/');
    if (!allowed) {
      window.location.assign('/inventario');
    }
    return;
  }

  if (role === 'CONSULTOR_SAI') {
    const allowed = path === '/soporte/sai' || path.startsWith('/soporte/sai/');
    if (!allowed) {
      window.location.assign('/soporte/sai');
    }
  }
}

async function _fetch<T>(
  path: string,
  init: RequestInit = {},
  opts?: { idempotent?: boolean },
): Promise<ApiResp<T>> {
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');
  const session = getSession();
  const token = session?.token ?? '';
  const user = getSessionUser();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const role = normalizeRole(user?.role ?? null);
  if (role === 'ADMIN' && !headers.has('X-Almacen-Id')) {
    const almacenId = resolveAdminAlmacenId();
    if (almacenId) headers.set('X-Almacen-Id', almacenId);
  }
  const vendorHeader = getVendorContextHeaderValue();
  if (vendorHeader && !headers.has('X-Vendor-Id')) {
    headers.set('X-Vendor-Id', vendorHeader);
  }
  const method = (init.method ?? 'GET').toUpperCase();
  if (opts?.idempotent && method === 'POST' && !headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', idemKey());
  }

  const target = path.startsWith('/') ? path : `/${path}`;
  const requestInit: RequestInit = { ...init, headers };
  if (method === 'GET' && !requestInit.cache) {
    requestInit.cache = 'no-store';
  }
  const url = `${BASE}${target}`;
  const response = await fetch(url, requestInit);
  const status = response.status;
  const hasBody = status !== 204 && status !== 304;
  let json: unknown = null;
  if (hasBody) {
    try {
      json = await response.json();
    } catch {
      json = null;
    }
  }

  if (status === 304) {
    return { ok: true, data: ((json as T) ?? ({} as T)), status };
  }

  if (!response.ok) {
    const payload =
      json && typeof json === 'object'
        ? (json as Partial<ApiErr>)
        : null;
    if (status === 401 && shouldLogoutOn401(url)) {
      handleUnauthorized(payload);
    }
    const error: ApiErr = {
      ok: false,
      code: typeof payload?.code === 'string' ? (payload.code as string) : 'http_error',
      message:
        typeof payload?.message === 'string'
          ? (payload.message as string)
          : response.statusText || 'HTTP error',
      details: payload?.details,
      status,
    };
    throw error;
  }

  return { ok: true, data: ((json as T) ?? ({} as T)), status };
}

export const api = {
  get: <T,>(path: string) => _fetch<T>(path),
  post: <T,>(path: string, body?: unknown, idemp = false) =>
    _fetch<T>(
      path,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
      { idempotent: idemp },
    ),
};

export function joinApi(path: string): string {
  const target = String(path ?? '');
  if (/^https?:\/\//i.test(target)) {
    return target;
  }
  const clean = target
    .replace(/^\/+/, '/')
    .replace(/^\/api\/+/i, '/');
  return `${API_BASE}${clean.startsWith('/') ? '' : '/'}${clean}`;
}

function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }
  return { ...(headers as Record<string, string>) };
}

function shouldSetJsonContentType(body: BodyInit | null | undefined): boolean {
  if (!body) return false;
  if (typeof body === 'string') return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return false;
  return true;
}

function isAuthEndpoint(url?: string | null): boolean {
  if (!url) return false;
  const normalized = url.toString();
  if (AUTH_ENDPOINTS.some(endpoint => normalized.includes(endpoint))) {
    return true;
  }
  return ['/auth/login', '/auth/refresh', '/auth/me'].some(fragment => normalized.includes(fragment));
}

function shouldLogoutOn401(url?: string | null): boolean {
  if (!url) return true;
  const normalized = url.toString();
  return !normalized.includes('/auth/login');
}

function handleUnauthorized(payload?: Partial<ApiErr> | null): void {
  const reason = payload?.code === 'SESSION_REVOKED' ? 'session_revoked' : 'session_expired';
  redirectToLogin({ reason });
}

type ApiFetchInit = RequestInit & { idempotent?: boolean };

export async function apiFetch(path: string, init: ApiFetchInit = {}): Promise<Response> {
  const storedToken = getSession()?.token ?? '';
  const { idempotent = false, ...rest } = init;
  const headers = new Headers(rest.headers ?? {});
  const method = (rest.method || 'GET').toUpperCase();

  if (idempotent && method === 'POST' && !headers.has('Idempotency-Key')) {
    const makeId = () =>
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `idemp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    headers.set('Idempotency-Key', makeId());
  }

  if (!headers.has('Content-Type') && shouldSetJsonContentType(rest.body)) {
    headers.set('Content-Type', 'application/json');
  }
  if (storedToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${storedToken}`);
  }
  const role = normalizeRole(getSessionUser()?.role ?? null);
  if (role === 'ADMIN' && !headers.has('X-Almacen-Id')) {
    const almacenId = resolveAdminAlmacenId();
    if (almacenId) headers.set('X-Almacen-Id', almacenId);
  }
  const vendorHeader = getVendorContextHeaderValue();
  if (vendorHeader && !headers.has('X-Vendor-Id')) {
    headers.set('X-Vendor-Id', vendorHeader);
  }

  const isAbsolute = typeof path === 'string' && /^https?:\/\//i.test(path);
  const isPrefixedApi = typeof path === 'string' && path.startsWith('/api/');
  const url = isAbsolute || isPrefixedApi ? (path as string) : joinApi(path);

  const nextInit: RequestInit = { ...rest, headers };
  if (method === 'GET' && !nextInit.cache) {
    nextInit.cache = 'no-store';
  }

  const response = await fetch(url, nextInit);

  if (response.status === 403) {
    try {
      const cloned = response.clone();
      void cloned
        .json()
        .then((data: any) => {
          const code = typeof data?.code === 'string' ? data.code : '';
          if (code !== 'FORBIDDEN') return;
          toast('No tienes permisos', 'warning');
          const role = normalizeRole(getSession()?.user?.role ?? null);
          redirectExternalReadOnlyRole(role);
        })
        .catch(() => {});
    } catch {
      // ignore clone/read errors
    }
  }

  if (response.status === 401) {
    if (shouldLogoutOn401(url)) {
      let payload: Partial<ApiErr> | null = null;
      try {
        payload = (await response.clone().json()) as Partial<ApiErr>;
      } catch {
        payload = null;
      }
      handleUnauthorized(payload);
    }
    throw new HttpError('Unauthorized', 401);
  }

  return response;
}

export const httpClient = axios.create({
  baseURL: API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE,
  timeout: 15000,
});

httpClient.interceptors.request.use(config => {
  const headers = normalizeHeaders(config.headers as HeadersInit | undefined);
  const token = getSession()?.token ?? '';
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  const role = normalizeRole(getSessionUser()?.role ?? null);
  if (role === 'ADMIN' && !headers['X-Almacen-Id'] && !headers['x-almacen-id']) {
    const almacenId = resolveAdminAlmacenId();
    if (almacenId) headers['X-Almacen-Id'] = almacenId;
  }
  const vendorHeader = getVendorContextHeaderValue();
  if (vendorHeader && !headers['X-Vendor-Id'] && !headers['x-vendor-id']) {
    headers['X-Vendor-Id'] = vendorHeader;
  }
  const nextHeaders: AxiosRequestHeaders = new AxiosHeaders(headers);
  config.headers = nextHeaders;
  return config;
});

httpClient.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status;
    const url = error?.config?.url ?? '';
    if (status === 401 && shouldLogoutOn401(url)) {
      handleUnauthorized(error?.response?.data ?? null);
    }
    if (status === 403) {
      const code = error?.response?.data?.code;
      if (code === 'FORBIDDEN') {
        toast('No tienes permisos', 'warning');
        const role = normalizeRole(getSession()?.user?.role ?? null);
        redirectExternalReadOnlyRole(role);
      }
    }
    if (error?.response) {
      const message =
        error.response.data?.message ||
        error.response.statusText ||
        `HTTP ${error.response.status}`;
      throw new HttpError(message, error.response.status, error.response.data);
    }
    throw error;
  },
);

export const get = <T = unknown>(url: string, config?: any) =>
  httpClient.get<T>(url, config).then(res => res.data as T);
export const post = <T = unknown>(url: string, data?: any, config?: any) =>
  httpClient.post<T>(url, data, config).then(res => res.data as T);
export const put = <T = unknown>(url: string, data?: any, config?: any) =>
  httpClient.put<T>(url, data, config).then(res => res.data as T);
export const patch = <T = unknown>(url: string, data?: any, config?: any) =>
  httpClient.patch<T>(url, data, config).then(res => res.data as T);
export const del = <T = unknown>(url: string, config?: any) =>
  httpClient.delete<T>(url, config).then(res => res.data as T);
